<template>
  <div class="service-form">
    <v-layout wrap>
      <v-flex class="xs12 md2 pa-3">
        <v-text-field
          v-model="service.attributes.name"
          label="Name"
          placeholder="name"
          name="name"
          required
        />
      </v-flex>
      <v-flex class="xs12 md4 pa-3" v-if="serviceCategories.length > 0">
        <v-select
          v-model="service.attributes.service_category_id"
          :items="serviceCategories"
          item-text="attributes.name"
          item-value="attributes.id"
          label="Service Category *"
          :error="!service.attributes.service_category_id"
        />
      </v-flex>
      <v-flex class="xs12 md4 pa-3">
        <v-text-field
          v-model="service.attributes.description"
          label="Description"
          placeholder="description"
          name="description"
          required
        />
      </v-flex>
      <v-flex class="xs12 md2 pa-3">
        <div class="checkbox-top-label ma-0">
          <label>Hidden from scheduler</label>
          <v-checkbox v-model="service.attributes.hidden"></v-checkbox>
          <help-tooltip
            tooltip="This add on will not be available on your online scheduler (i.e. only you and your staff can add it.)"
          />
        </div>
      </v-flex>
      <template v-if="expanded">
        <v-flex :class="`${secondRowInputClassNames} xs12 pa-3`">
          <v-select
            v-model="service.attributes.templates"
            :items="templates"
            item-text="attributes.name"
            item-value="id"
            label="Template(s)"
            multiple
          />
        </v-flex>
        <v-flex
          v-if="hasAgreements"
          :class="`${secondRowInputClassNames} xs12 pa-3`"
        >
          <v-select
            v-model="service.attributes.agreements"
            :items="agreements"
            item-text="attributes.name"
            item-value="id"
            label="Agreement(s)"
            multiple
          />
        </v-flex>
        <v-flex class="xs12 md2 pa-3">
          <div v-if="hasPayments" class="input-field dollar-overlay vue ma-0">
            <span class="sym">{{ company.attributes.currency_sym }}</span>
            <v-text-field
              min="0.00"
              step="0.01"
              placeholder="0.00"
              v-model="service.attributes.amount"
              type="number"
              label="Base Cost"
            />
          </div>
        </v-flex>
        <v-flex class="xs12 md2 pa-3">
          <div class="input-field time-overlay ma-0">
            <v-text-field
              min="0.0"
              step="0.25"
              placeholder="0.0"
              v-model="service.attributes.duration"
              type="number"
              label="Base Duration (HRs)"
            />
          </div>
        </v-flex>
        <v-flex
          v-if="companyEvents"
          :class="`${secondRowInputClassNames} xs12 pa-3 has-tooltip`"
        >
          <v-text-field
            v-model="eventsString"
            placeholder="Event Names (comma separated)"
            label="Default Inspection Events"
          />
          <help-tooltip
            tooltip="These events will automatically be added for you when scheduling an inspection with this service."
          />
        </v-flex>
        <organization-service-id-input
          classes="md4 pa-3"
          :organization-service-id.sync="organizationServiceId"
          :show="canShowServiceIdInput"
        />
        <v-flex v-if="hasPayAtClose" class="xs12 pa-3">
          <h5>Payments</h5>
          <v-flex xs12>
            <div>
              <v-checkbox
                v-model="service.attributes.settings.enable_pay_at_close"
                label="Pay at Close: Inspections with this service will display Pay at Close as a payment option."
              />
            </div>
          </v-flex>
        </v-flex>
        <v-flex v-if="hasIntegrations" class="xs12 pa-3">
          <h5>Integrations</h5>
          <v-flex xs12>
            <div v-if="companySettings.internachi_buyback">
              <v-checkbox
                v-model="service.attributes.settings.internachi_buyback"
                label="InterNACHI BuyBack: Inspections with this service will be submitted to InterNACHI BuyBack by default."
              />
            </div>
          </v-flex>
          <v-flex xs12>
            <div v-if="companySettings.repair_pricer">
              <v-select
                v-model="service.attributes.settings.repair_pricer_template_ids"
                :items="templates"
                item-text="attributes.name"
                item-value="id"
                label="Repair Pricer: Templates to send to Repair Pricer on publishing."
                multiple
              />
            </div>
          </v-flex>
          <v-flex xs12>
            <div v-if="companySettings.secure_24">
              <v-checkbox
                v-model="service.attributes.settings.secure_24"
                label="Secure24: Inspections with this service will auto-send to Secure24 upon publishing."
              />
            </div>
          </v-flex>
          <v-flex xs12>
            <div v-if="companyRws">
              <v-checkbox
                v-model="service.attributes.settings.rws"
                label="RWS: This service will default to send to RWS the morning of the inspection."
              />
            </div>
          </v-flex>
          <v-flex xs12>
            <div v-if="companySettings.homebinder">
              <v-checkbox
                v-model="service.attributes.settings.homebinder"
                label="HomeBinder: This service will default to send to HomeBinder once published, paid for, and agreements are signed."
              />
            </div>
          </v-flex>
        </v-flex>
        <v-flex v-if="hasClassifications" class="xs12 pa-3">
          <h5>Service Category</h5>
          <v-select
            v-model="service.attributes.service_classification_id"
            :items="serviceClassifications"
            item-text="attributes.name"
            item-value="attributes.id"
          />
        </v-flex>
        <v-flex class="xs12 px-3 pb-3 mr-3">
          <v-expansion-panel>
            <v-expansion-panel-content
              :id="serviceFormId"
              class="service-form-modifiers"
              expand-icon=""
            >
              <div class="service-modifiers-header" slot="header">
                <div class="inline">
                  <div class="fa fa-chevron-right light" />
                  <h5>Modifiers</h5>
                </div>

                <p class="light small">
                  Add additional fees &amp; hours to your service when the
                  property matches certain criteria like square footage or age.
                </p>
              </div>
              <v-flex class="xs12 pa-3">
                <ul>
                  <li
                    is="modifier-item"
                    :key="modifier.id"
                    :modifier="modifier"
                    v-for="modifier in filteredServiceModifiers"
                  />
                </ul>
                <div class="col s12 center-align">
                  <div
                    class="btn btn-blank btn-flat mt-3"
                    @click="createServiceModifier"
                  >
                    <div class="fa fa-plus" />
                    Modifiers
                  </div>
                </div>
              </v-flex>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-flex>
        <v-flex class="xs12 pa-3">
          <h5>Add-Ons</h5>
          <p class="light small">
            Give your client options to add additional services and upsells.
          </p>
          <ul>
            <draggable
              :options="{ handle: '.btn-reorder-add-ons' }"
              v-model="addOnContainer"
              @end="updatePriority"
            >
              <li
                is="add-on-item"
                :key="addon.id"
                :addon="addon"
                :addOnModifiers="addon.serviceModifiers"
                v-for="addon in addOnContainer"
              />
            </draggable>
          </ul>
          <div class="col s12 center-align">
            <div
              class="btn btn-blank btn-flat mt-3"
              @click="createServiceAddOn"
            >
              <div class="fa fa-plus" />
              Add-On
            </div>
          </div>
        </v-flex>

        <v-flex v-if="hasPayments" class="xs12 pa-3">
          <h5>Taxes</h5>
          <p class="light small">
            Add a percentage to the total for this service. Taxes are calculated
            after all modifiers and add-ons are processed.
          </p>
          <ul>
            <draggable
              :options="{ handle: '.btn-reorder-taxes' }"
              v-model="taxesContainer"
              @end="updatePriority"
            >
              <li
                is="tax-item"
                :key="tax.id"
                :tax="tax"
                v-for="tax in taxesContainer"
              />
            </draggable>
          </ul>
          <div class="col s12 center-align">
            <div class="btn btn-blank btn-flat mt-3" @click="createServiceTax">
              <div class="fa fa-plus" />
              Tax
            </div>
          </div>
        </v-flex>

        <v-flex class="xs12 pa-3">
          <h5>Partnership Offers</h5>
          <p class="light small" v-if="companySettings.enable_partnerships">
            Select which company <b>enabled</b> Partners to show on the Client
            Portal for this service by default. You will still have the option
            to select which partnership offers are shown on a per inspection
            basis. You can enable and learn more about partners on this page
            <a href="/partnerships">Partnerships</a>.
          </p>
          <p class="light small" v-else>
            Earn additional revenue on inspections by enabling
            <a href="/partnerships">Partnerships</a>. Once enabled you can
            select which Partners to show on the Client Portal for this service
            by default. You will still have the option to select which
            partnership offers are shown on a per inspection basis.
          </p>

          <v-layout v-if="companySettings.enable_partnerships">
            <v-flex
              xs12
              md3
              px-2
              v-for="partnership in partnerships"
              :key="partnership.name"
            >
              <div :class="`fa fa-${partnership.icon}`" />
              {{ partnership.name }}

              <div v-for="partner in partnership.partners" :key="partner.name">
                <v-tooltip
                  bottom
                  max-width="300"
                  content-class="help-content"
                  class="help-tooltip-container"
                  :disabled="companySettings[partner.value]"
                  close-delay="3000"
                  :open-on-focus="true"
                >
                  <v-checkbox
                    slot="activator"
                    v-model="service.attributes.settings[partner.value]"
                    :label="partner.name"
                    :disabled="!companySettings[partner.value]"
                  ></v-checkbox>

                  <span>
                    This partner is currently disabled by your company.
                    <span v-if="userProfile.attributes.is_company_admin"
                      >Enable it
                      <a class="fg-dark" href="/partnerships">here</a>.</span
                    >
                  </span>
                </v-tooltip>
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
      </template>
    </v-layout>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import AddOnItem from './form_components/AddOnItem'
import Draggable from 'vuedraggable'
import HelpTooltip from '../shared/HelpTooltip'
import ModifierItem from './form_components/ModifierItem'
import ServiceAddOn from '../../models/ServiceAddOn'
import ServiceModifier from '../../models/ServiceModifier'
import ServiceTax from '../../models/ServiceTax'
import TaxItem from './form_components/TaxItem'
import OrganizationServiceIdInput from './form_components/OrganizationServiceIdInput'
import dig from '../../Dig'

export default {
  components: {
    AddOnItem,
    Draggable,
    HelpTooltip,
    ModifierItem,
    OrganizationServiceIdInput,
    TaxItem,
  },
  computed: {
    ...mapState('services', [
      'agreements',
      'company',
      'serviceAddOns',
      'serviceCategories',
      'serviceClassifications',
      'serviceModifiers',
      'serviceTaxes',
      'templates',
      'userProfile',
    ]),
    canShowServiceIdInput() {
      return (
        this.hasOrganizationId &&
        this.company.attributes.settings.enable_advanced
      )
    },
    companyEvents() {
      return this.companySettings.enable_inspection_events
    },
    companyRws() {
      return this.companySettings.rws
    },
    companyS24Autosend() {
      return (
        this.companySettings.secure_24 &&
        this.companySettings.secure_24_autosend
      )
    },
    companySettings() {
      return dig(this, 'company.attributes.settings', {})
    },
    filteredServiceModifiers() {
      return this.serviceModifiers.filter((modifier) => {
        return (
          modifier.attributes.modifiable_id === this.service.attributes.id &&
          modifier.attributes.modifiable_type === 'Service'
        )
      })
    },
    hasIntegrations() {
      return [
        this.companySettings.internachi_buyback,
        this.companySettings.repair_pricer,
        this.companySettings.rws,
        this.companyS24Autosend,
        this.companySettings.homebinder,
      ].some((x) => !!x)
    },
    hasPayAtClose() {
      return this.companySettings.enable_pay_at_close
    },
    hasAgreements() {
      return this.companySettings.enable_agreements
    },
    hasClassifications() {
      return this.companySettings.enable_spectora_network
    },
    hasOrganizationId() {
      return !!this.company.attributes.organization_id
    },
    hasPayments() {
      return this.companySettings.enable_payments
    },
    organizationServiceId: {
      get() {
        return this.service.attributes.settings.organization_service_id
      },
      set(value) {
        this.$set(
          this.service.attributes.settings,
          'organization_service_id',
          value
        )
      },
    },
    secondRowInputClassNames() {
      return this.canShowServiceIdInput ? 'md3' : 'md4'
    },
    serviceFormId() {
      return `service-form-${this.service.attributes.id}`
    },
  },
  data() {
    return {
      addOnContainer: [],
      eventsString: '',
      taxesContainer: [],
      partnerships: [
        {
          name: 'MOVING SERVICES',
          icon: 'truck',
          partners: [{ name: 'PODS', value: 'enable_pods' }],
        },
        {
          name: 'HOME SECURITY',
          icon: 'home',
          partners: [{ name: 'Secure24', value: 'enable_secure24' }],
        },
        {
          name: 'HOME INSURANCE',
          icon: 'umbrella',
          partners: [{ name: 'Acrisure', value: 'enable_acrisure' }],
        },
        {
          name: 'HOME WARRANTY',
          icon: 'shield',
          partners: [{ name: 'American HomeShield', value: 'enable_ahs' }],
        },
      ],
      rules: {
        organization_service_id: {
          counter: (value) => value.length <= 50 || 'Max 50 characters',
          format: (value) =>
            value.match(/^[a-zA-Z0-9-_()]*$/) ||
            'Allowed characters: letters, numbers, and - ( ) _',
        },
      },
    }
  },
  methods: {
    createServiceAddOn() {
      let serviceAddOn = JSON.parse(JSON.stringify(ServiceAddOn))
      serviceAddOn.attributes.service_id = this.service.id
      serviceAddOn.attributes.priority = this.addOnContainer.length
      this.$store.dispatch('services/createServiceAddOn', serviceAddOn)
    },
    createServiceModifier() {
      let serviceModifier = JSON.parse(JSON.stringify(ServiceModifier))
      serviceModifier.attributes.modifiable_id = this.service.id
      serviceModifier.attributes.modifiable_type = 'Service'
      this.$store.dispatch('services/createServiceModifier', serviceModifier)
    },
    createServiceTax() {
      let serviceTax = JSON.parse(JSON.stringify(ServiceTax))
      serviceTax.attributes.service_id = this.service.id
      serviceTax.attributes.priority = this.taxesContainer.length
      this.$store.dispatch('services/createServiceTax', serviceTax)
    },
    filterAndSortValue(items) {
      return items
        .filter((item) => {
          return item.attributes.service_id === this.service.attributes.id
        })
        .sort((a, b) => {
          return a.attributes.priority - b.attributes.priority
        })
    },
    updatePriorities(event, type) {
      this[`${type}Container`].forEach((item, idx) => {
        if (item.attributes.priority !== idx) {
          let updatedItem = JSON.parse(JSON.stringify(item))
          updatedItem.attributes.priority = idx

          if (type === 'taxes') {
            this.$store.dispatch('services/updateServiceTax', updatedItem)
          } else {
            this.$store.dispatch('services/updateServiceAddOn', updatedItem)
          }
        }
      })
    },
    updatePriority(event) {
      const idPair = event.item.id.split('-')

      if (idPair[0] === 'tax') {
        this.updatePriorities(event, 'taxes')
      } else if (idPair[0] === 'addon') {
        this.updatePriorities(event, 'addOn')
      }
    },
  },
  mounted() {
    this.taxesContainer = this.serviceTaxes.filter((tax) => {
      return tax.attributes.service_id === this.service.attributes.id
    })

    this.addOnContainer = this.serviceAddOns.filter((addon) => {
      return addon.attributes.service_id === this.service.attributes.id
    })

    this.eventsString = this.service.attributes.events.join(',')
  },
  watch: {
    eventsString(newV, oldV) {
      this.service.attributes.events = (newV || '')
        .split(',')
        .filter((e) => e.length > 0)
        .map((e) => e.trim())
    },
    service: {
      handler: debounce(function (newVal) {
        this.$store.dispatch('services/updateService', newVal)
      }, 500),
      deep: true,
    },
    serviceAddOns: {
      handler: function (newVal, oldVal) {
        if (newVal.length !== oldVal.length) {
          this.addOnContainer = this.filterAndSortValue(newVal)
        }
      },
      deep: true,
    },
    serviceTaxes: {
      handler: function (newVal, oldVal) {
        if (newVal.length !== oldVal.length) {
          this.taxesContainer = this.filterAndSortValue(newVal)
        }
      },
      deep: true,
    },
  },
  props: ['service', 'expanded'],
}
</script>

<style scoped>
.dollar-overlay:before {
  bottom: auto;
}

.service-form {
  background-color: #fff;
  border-top: 1px solid #ddd;
}

.time-overlay:before {
  bottom: auto;
}

.checkbox-top-label {
  position: relative;
}
.checkbox-top-label label {
  top: -1rem;
  width: 300%;
  position: absolute;
  color: rgba(0, 0, 0, 0.54);
}

.help-tooltip {
  font-size: 1.25em;
  color: #65c635;
}

.help-content {
  color: #fff;
  font-size: 1em;
  text-align: center;
  background-color: #65c635;
}

.service-form-modifiers.v-expansion-panel__container--active .fa-chevron-right {
  transform: rotate(90deg);
}
</style>

<style>
#services-page .v-select .v-input__slot:before {
  border-width: 1px;
}

#services-page .v-label {
  overflow: initial;
}
</style>
